import React, { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "./styles/Booking.css"; // Import the styles
import Calendar from "../components/Calendar";
import Question from "../components/Question";
import { loadStripe } from "@stripe/stripe-js";
import Footer from "../components/Footer";

const Booking = (darken) => {
    const { isAuthenticated, isLoading, user } = useAuth0();
    const [activeQuestion, setActiveQuestion] = useState(1);
    const [selectedTech, setSelectedTech] = useState("");
    const [bunnyPeeking, setBunnyPeeking] = useState(false);
    const [previousQuestions, setPreviousQuestions] = useState([]);
    const [durationList, setDurationList] = useState([0, 0, 0, 0]);
    const [selected, setSelected] = useState([]);
    const [answers, setAnswers] = useState([])
    const [display, setDisplay] = useState("");
    const [formComplete, setFormComplete] = useState(false);
    const [highlight, setHighlight] = useState(false);
    const [borderHighlight, setBorderHighlight] = useState(false);
    const [instagramHighlight, setInstagramHighlight] = useState(false);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [loadingStripe, setLoadingStripe] = useState(false);
    const [durationEstimation, setDurationEstimation] = useState(0);
    const [costEstimation, setCostEstimation] = useState(0.00);
    const [instagram, setInstagram] = useState("");
    const [skips, setSkips] = useState(0);

    const calendarRef = useRef();

    const stripePromise = loadStripe("pk_live_51ROWMUDCPHdXZy4bcLH6Ql7a1nIlAY10PWPp4mKXqSSTOx2vgfQyFVLLSzEKF8gSe5e1SXQDl1n07bnQ8mtAZgAm00T9wTCVKS");

    const estimationRange = 10;

    useEffect(() => {
        const checkPaymentStatus = async () => {
            const sessionId = new URLSearchParams(window.location.search).get("session_id");

            if (sessionId) {
                try {
                    const response = await fetch(`https://bunny-klawz.uc.r.appspot.com/payment-status/${sessionId}`);
                    const data = await response.json();

                    if (data.status === "completed") {
                        setPaymentComplete(true);
                        console.log("✅ Payment confirmed:", data);
                    }
                } catch (error) {
                    console.error("❌ Error fetching payment status:", error);
                }
            }
        };

        checkPaymentStatus();
    }, []);


    useEffect(() => {
        if (display === "appointment-details") {
            let tempSelectedList = selected;
            tempSelectedList[4] = selectedTech;
            setSelected(tempSelectedList);

            setDurationEstimation(answers.reduce((runningTotal, x) => runningTotal + x.time, 0));
            setCostEstimation((answers.reduce((runningTotal, x) => runningTotal + x.price, 0)) - 10);
        }
    }, [selectedTech]);

    const handlePayment = async () => {
        if (!instagram) {
            timedInstagramHighlight();
            return;
        }

        console.log("🟡 Initiating Stripe Payment...");
        setLoadingStripe(true);

        try {
            const overlapCheck = await fetch("https://bunny-klawz.uc.r.appspot.com/appointments/check-overlap", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ appointmentDetails: selected }),
            });

            const overlapResult = await overlapCheck.json();

            if (overlapResult?.overlap) {
                const confirmRefresh = window.confirm("⚠️ This page timed out. Please refresh and try again.");
                if (confirmRefresh) {
                    window.location.reload();
                }
                return;
            }

            // ✏️ Update app_metadata.instagram on Auth0
            const updateMetadataResponse = await fetch(`https://bunny-klawz.uc.r.appspot.com/users/${encodeURIComponent(user.sub)}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    app_metadata: {
                        instagram: instagram
                    }
                }),
            });

            if (!updateMetadataResponse.ok) {
                throw new Error("Failed to update user metadata.");
            }

            console.log("✅ Instagram handle saved to Auth0");

            // 💳 Create Stripe session
            const response = await fetch("https://bunny-klawz.uc.r.appspot.com/create-checkout-session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    instagram: instagram,
                    user: user,
                    appointmentDetails: selected,
                    localDateTime: formatDate(selected[5].date),
                    answers: answers
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const session = await response.json();
            console.log("✅ Stripe Session Created:", session);

            const stripe = await stripePromise;
            const result = await stripe.redirectToCheckout({ sessionId: session.sessionId });

            if (result.error) {
                console.error("❌ Stripe Error:", result.error);
            }

        } catch (error) {
            console.error("❌ Error initiating payment:", error);
            const confirmRefresh = window.confirm("⚠️ This page timed out. Please refresh and try again.");
            if (confirmRefresh) {
                window.location.reload();
            }
        }
    };

    const scrollToTop = () => {
        if (darken.scrollRef.current) {
            darken.scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const scrollToCalendar = () => {
        if (calendarRef.current) {
            calendarRef.current.scrollIntoView({ top: 0, behavior: "smooth" });
        }
    };

    const timedHighlight = () => {
        setHighlight(true);
        setTimeout(() => {
            setHighlight(false);
        }, 2000);
    };

    const timedInstagramHighlight = () => {
        setInstagramHighlight(true);
        setTimeout(() => {
            setInstagramHighlight(false);
        }, 2000);
    };

    const timedBorderHighlight = () => {
        setBorderHighlight(true);
        setTimeout(() => {
            setBorderHighlight(false);
        }, 2000);
    };

    function formatDate(timestamp) {
        const date = new Date(timestamp);

        // Formatting components
        const month = date.toLocaleString('en-US', { month: 'short' }); // "Feb"
        const day = date.getDate(); // 28
        const year = date.getFullYear(); // 2025
        const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase(); // "8:00am"

        return `${month} ${day}, ${year} at ${time}`;
    }


    return (
        <div className="booking-container">
            {(!formComplete && <>
                <div className="booking-header">
                    <h2>Booking an Appointment</h2>
                    <p>
                        Please click{" "}
                        <a
                            href="https://www.instagram.com/bunnyklawz"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            here
                        </a>{" "}
                        to DM @bunnyklawz on Instagram for any questions!
                    </p>
                </div>

                {(activeQuestion !== 5 && <Question scrollRef={darken.scrollRef} activeQuestion={activeQuestion} setActiveQuestion={setActiveQuestion} previousQuestions={previousQuestions} setPreviousQuestions={setPreviousQuestions} durationList={durationList} setDurationList={setDurationList} selected={selected} setSelected={setSelected} answers={answers} setAnswers={setAnswers} skips={skips} setSkips={setSkips} />)}

                {(activeQuestion === 5 && <>
                    <div className="questions calendar-page fade">
                        {/* Preferred Nail Tech Selection */}
                        <div>
                            <h3>Do you have a preferred nail tech?</h3>
                            <p>
                                <span className={`${highlight ? "textHighlight" : ""}`}>Please select one.</span>
                            </p>
                            <div className={`options ${highlight ? "highlight" : ""}`}>
                                <label>
                                    <input
                                        type="radio"
                                        name="nail-tech"
                                        value="Leah"
                                        checked={selectedTech === "Leah"}
                                        onChange={() => {
                                            setSelectedTech("Leah");
                                            setDisplay("appointment-details");
                                            scrollToCalendar();
                                        }}
                                    />
                                    I want Leah!
                                </label>

                                <label>
                                    <input
                                        type="radio"
                                        name="nail-tech"
                                        value="Kayla"
                                        checked={selectedTech === "Kayla"}
                                        onChange={() => {
                                            setSelectedTech("Kayla");
                                            setDisplay("appointment-details");
                                            scrollToCalendar();
                                        }}
                                    />
                                    I want Kayla!
                                </label>

                                <label>
                                    <input
                                        type="radio"
                                        name="nail-tech"
                                        value="Kelly"
                                        checked={selectedTech === "Kelly"}
                                        onChange={() => {
                                            setSelectedTech("Kelly");
                                            setDisplay("appointment-details");
                                            scrollToCalendar();
                                        }}
                                    />
                                    I want Kelly!
                                </label>

                                <label>
                                    <input
                                        type="radio"
                                        name="nail-tech"
                                        value="No preference"
                                        checked={selectedTech === "No preference"}
                                        onChange={() => {
                                            setSelectedTech("No preference")
                                            setDisplay("appointment-details");
                                            scrollToCalendar();
                                        }}
                                    />
                                    I have no preference.
                                </label>
                            </div>
                        </div>
                        {((selectedTech !== "") && <div className="schedule-description fade">
                            <h3>Please see {!((selectedTech === "") || (selectedTech === "No preference")) && selectedTech + "’s "} appointments below and select your preferred slot.</h3>
                            <p>Select a date on the calendar to view availability for a specific day. </p>
                        </div>)}
                    </div>
                    <br />
                    <Calendar scrollRef={darken.scrollRef} calendarRef={calendarRef} selectedTech={selectedTech} setSelectedTech={setSelectedTech} selected={selected} setSelected={setSelected} borderHighlight={borderHighlight} answers={answers} />
                    <div className="spacer"></div>
                    <div className="booking-page questions-nav">
                        <button
                            className="go-back"
                            onClick={() => {
                                let previousQuestion = previousQuestions[previousQuestions.length - 1];
                                setActiveQuestion(previousQuestion)

                                let tempPS = previousQuestions;
                                tempPS.pop();
                                setPreviousQuestions(tempPS);

                                setSelectedTech("");

                                scrollToTop();
                            }}>Go Back</button>
                        <div className="continue">
                            <img className={`continue-bunny ${bunnyPeeking ? "hovered" : ""}`} src="/images/ContinueBunny.png" alt="Continue Bunny" />
                            <button
                                className="request-button continue-button"
                                onMouseEnter={() => setBunnyPeeking(true)}
                                onMouseLeave={() => setBunnyPeeking(false)}
                                onClick={() => {

                                    console.log(activeQuestion, selected);

                                    if (!selected[activeQuestion].artist) {
                                        timedHighlight();
                                        return;
                                    }
                                    if (!selected[activeQuestion].date) {
                                        timedBorderHighlight();
                                        return;
                                    }
                                    setFormComplete(true);
                                    darken.setDarken(true);

                                    scrollToTop();
                                }
                                }>
                                Request Appointment
                            </button>
                        </div>
                    </div>
                    <Footer />
                </>)}
            </>)}
            {(formComplete && <>
                <div className="booking-header">
                    <h2>Booking an Appointment</h2>
                    <p>
                        Please click{" "}
                        <a
                            href="https://www.instagram.com/bunnyklawz"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            here
                        </a>{" "}
                        to DM @bunnyklawz on Instagram for any questions!
                    </p>
                </div>
                <>
                    <div className="questions calendar-page fade">
                        {/* Preferred Nail Tech Selection */}
                        <div>
                            {(!paymentComplete ? <>
                                {/* {(selected[5] && <div className="flex appointment"><h3>Selected Appointment: </h3><h3>{(formatDate(selected[5]))}</h3></div>)} */}
                                <div className="split-columns">
                                    <div>
                                        <h3 className="details-header">Appointment Details </h3>
                                        {(selected[0] && <div className="flex sub"><h3>Service: </h3><div><p>{(selected[0])}</p> {(selected[1] && <p>{(selected[1])}</p>)}</div></div>)}
                                        {(selected[2] && <div className="flex sub"><h3>Design: </h3><p>{selected[2] !== "I do not want any designs." ? (selected[2]) : ("None")}</p> </div>)}
                                        {(selected[3] && <div className="flex sub"><h3>Soak-Offs: </h3><p>{selected[3] !== "I will not need a soak-off." ? (selected[3]) : ("None")}</p> </div>)}
                                        {(selected[5].artist && <div className="flex sub"><h3>Nail Tech: </h3><p>{(selected[5].artist)}</p> </div>)}
                                        <div className="flex sub"><h3>Estimated Duration: </h3><p>{(durationEstimation)} minutes</p></div>
                                        <div className="flex sub"><h3>Date: </h3><p>{(formatDate(selected[5].date))}</p></div>
                                    </div>
                                    <div>
                                        <h3 className="details-header">Payment Details </h3>
                                        <div className="flex sub"><h3>Pay Now: </h3><p>$10</p></div>
                                        <div className="flex sub"><h3>Estimated Pay: <br /> (at appointment) </h3><p>${(costEstimation)}-${(costEstimation + estimationRange)}</p></div>
                                    </div>
                                </div>

                                <div className="ig-box">
                                    <h3><span className={`${instagramHighlight ? "highlight" : ""}`}>Please enter your Instagram handle!</span> We will only contact you via Instagram for any emergency cancellations and rescheduling! </h3>
                                    <input
                                        type="text"
                                        placeholder="@yourinstagramaccount"
                                        className={`${instagramHighlight ? "highlight" : ""}`}
                                        value={instagram}
                                        onChange={(event) => {
                                            let value = event.target.value;

                                            // Remove all spaces
                                            value = value.replaceAll(" ", "");

                                            // Ensure it starts with '@'
                                            if (!value.startsWith("@")) {
                                                value = "@" + value.replace(/^@+/, ""); // avoid multiple '@'
                                            }

                                            // Limit to 33 characters
                                            if (value.length > 33) {
                                                value = value.slice(0, 33);
                                            }

                                            setInstagram(value);
                                        }}
                                    />
                                    <div className="question1 warning fade"><p className="warning-text"><img src="/images/Warning.png" alt="Warning" />Please note automated messages for appointment and deposit confirmations/updates will be sent to the contact information provided on Stripe. Responses to these messages won't be monitored, so for direct communication, please reach out to us on Instagram.</p></div>
                                </div>
                            </> :
                                <div>
                                    {/* <h3 className="payment-confirmation-instagram">@{instagram}</h3> */}
                                    <h1 className="payment-confirmation-message">Your appointment request has been sent. Please give us 24 hours to confirm your deposit and appointment. Thank you so much!</h1>
                                </div>
                            )}
                        </div>
                    </div>
                    <br />
                    <div className="spacer"></div>
                    <div className="booking-page questions-nav">
                        {(!paymentComplete ?
                            <>
                                <button
                                    className="go-back"
                                    onClick={() => {
                                        setFormComplete(false);
                                        setSelectedTech("");
                                        darken.setDarken(false);
                                        scrollToTop();
                                    }}>
                                    Go Back
                                </button>
                                <div className="continue">
                                    <img className={`continue-bunny ${bunnyPeeking ? "hovered" : ""}`} src="/images/ContinueBunny.png" alt="Continue Bunny" />
                                    <button
                                        className="request-button continue-button"
                                        onMouseEnter={() => setBunnyPeeking(true)}
                                        onMouseLeave={() => setBunnyPeeking(false)}
                                        onClick={handlePayment}
                                    >
                                        {!loadingStripe ? ("Submit and Pay") : ("Loading...")}
                                    </button>
                                </div>
                            </> :
                            <></>
                        )}
                    </div>
                    <Footer />
                </>
            </>)}
        </div>
    );
};

export default Booking;